import {
    ModelSearchResult,
    searchModels,
} from '@internal/services/machineSearchClient';
import { localeNamespace } from '@internal/utils/machine/i18n/Locale';
import { MachineType } from '@internal/utils/machine/MachineType';
import { useModelLabel } from '@internal/utils/machine/useModelLabel';
import {
    LoadValuesHandler,
    RemoteAutoComplete,
} from '@renderer-ui-library/molecules/RemoteAutocomplete/RemoteAutoComplete';
import { SelectFilterOption } from '@renderer-ui-library/molecules/RemoteAutocomplete/SelectFilterOption';
import { translations } from 'i18n/translations';
import { uniqBy } from 'lodash';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { SearchModel } from '../SearchResultsBlock/TSearchParams';

type ModelSelectFilterOption = SelectFilterOption & {
    type: SearchModel['type'];
};

type Props = {
    onChange: (model: SearchModel[]) => void;
    models: SearchModel[];
    disabled?: boolean;
    closeOnSelect?: boolean;
    brands: string[];
    machineType: MachineType | null;
    showSelectedFuzzyModel?: boolean;
};

export const ModelSelectFilter: React.FC<Props> = React.memo(props => {
    const { t } = useTranslation(localeNamespace.common);
    const {
        brands,
        models,
        disabled,
        onChange,
        closeOnSelect,
        machineType,
        showSelectedFuzzyModel,
    } = props;
    const brandsRef = useRef(brands);
    useEffect(() => {
        brandsRef.current = brands;
    }, [brands]);

    const { getModelLabel } = useModelLabel();

    const modelSearchResultsToModelFilterOptions = useCallback(
        (
            modelSearchResults: ModelSearchResult[],
        ): ModelSelectFilterOption[] => {
            const brandsMap = modelSearchResults.reduce<{
                [brand: string]: { models: ModelSearchResult[] };
            }>((map, entry) => {
                if (!map[entry.BRAND]) {
                    map[entry.BRAND] = { models: [] };
                }
                // biome-ignore lint/style/noNonNullAssertion: legacy
                map[entry.BRAND]!.models.push(entry);
                return map;
            }, {});

            const values = Object.values(brandsMap).flatMap(({ models }) =>
                uniqBy(
                    models.flatMap(
                        (modelSearchResult): ModelSelectFilterOption[] => {
                            const values: ModelSelectFilterOption[] = [];
                            if (
                                modelSearchResult.MODEL_SERIES &&
                                modelSearchResult.MODEL_SERIES !==
                                    modelSearchResult.MODEL
                            ) {
                                values.push({
                                    label: getModelLabel(
                                        modelSearchResult.MODEL_SERIES,
                                        'series',
                                    ),
                                    value: modelSearchResult.MODEL_SERIES,
                                    type: 'series',
                                    group: modelSearchResult.BRAND,
                                });
                            }

                            if (
                                modelSearchResult.BASE_MODEL !==
                                modelSearchResult.MODEL
                            ) {
                                values.push({
                                    label: modelSearchResult.BASE_MODEL,
                                    value: modelSearchResult.BASE_MODEL,
                                    type: 'base',
                                    group: modelSearchResult.BRAND,
                                });
                            }

                            values.push({
                                label: modelSearchResult.MODEL,
                                value: modelSearchResult.MODEL,
                                type: 'mapped',
                                group: modelSearchResult.BRAND,
                            });
                            return values;
                        },
                    ),
                    e => e.value,
                ),
            );

            return values;
        },
        [getModelLabel],
    );

    const handleChange = useCallback(
        (value: ModelSelectFilterOption[] | string | null) => {
            if (!value) {
                onChange([]);
                return;
            }

            if (typeof value === 'string') {
                onChange([
                    {
                        name: value,
                        type: 'fuzzy',
                    },
                ]);
            } else {
                onChange(
                    value.map(v => ({
                        name: v.value,
                        type: v.type,
                    })),
                );
            }
        },
        [onChange],
    );

    const value = useMemo(
        () =>
            models.reduce<ModelSelectFilterOption[]>(
                (list, model): ModelSelectFilterOption[] => {
                    if (showSelectedFuzzyModel || model.type !== 'fuzzy') {
                        list.push({
                            value: model.name,
                            label: getModelLabel(model.name, model.type),
                            type: model.type,
                        });
                    }
                    return list;
                },
                [],
            ),
        [getModelLabel, models, showSelectedFuzzyModel],
    );

    const loadValues = useCallback<LoadValuesHandler<ModelSelectFilterOption>>(
        async ({ input, abortSignal }): Promise<ModelSelectFilterOption[]> => {
            try {
                const lists =
                    brandsRef.current.length > 0
                        ? await Promise.all(
                              brandsRef.current.map(brand =>
                                  searchModels({
                                      query: input,
                                      brand,
                                      abortSignal,
                                      machineType: machineType ?? undefined,
                                  }),
                              ),
                          )
                        : [
                              await searchModels({
                                  query: input,
                                  abortSignal,
                                  machineType: machineType ?? undefined,
                              }),
                          ];

                const options = modelSearchResultsToModelFilterOptions(
                    lists.flat(),
                );

                // always add keyword option to the end
                options.push({
                    label: getModelLabel(input, 'fuzzy'),
                    value: input,
                    type: 'fuzzy',
                    group: t(translations.searchGroupKeywordTitle),
                });

                return options;
            } catch (_err) {
                return [];
            }
        },
        [getModelLabel, machineType, modelSearchResultsToModelFilterOptions, t],
    );

    return (
        <RemoteAutoComplete<ModelSelectFilterOption>
            valuesReference={brands.join(',')}
            type="multiple"
            value={value}
            onChange={handleChange}
            loadValues={loadValues}
            disabled={disabled}
            inputPlaceholder={t(translations.searchResultsFiltersModelLabel)}
            freeSolo
            autoHighlight
            grouping
            disableCloseOnSelect={!closeOnSelect}
        />
    );
});

ModelSelectFilter.displayName = 'ModelSelectFilter';
